'use strict';

// ─────────────────────────────────────────────────────────────────────────────
// Behaviour for the cart drawer's line items (markup: snippets/custom-line-item.liquid).
//
// Loaded as an asset from sections/custom-cart-drawer.liquid rather than inline in the line-item
// snippet, because that snippet only renders inside {% for line_item in cart.items %}: with an empty
// cart it never ran, and CartDrawer._onCartRefresh() rebuilds the drawer with `div.innerHTML = …`,
// which never executes <script> tags. So a cart that went 0 → 1 items through cart:refresh (the
// flavour picker's add path, a bfcache restore, the discount field) ended up with line items whose
// elements were never defined — the trash button and the ± stepper did nothing until a page reload
// put a line item, and therefore the script, in the server-rendered HTML.
// ─────────────────────────────────────────────────────────────────────────────

if (!window.customElements.get('custom-line-item-quantity')) {
  class CustomLineItemQuantity extends HTMLElement {
    connectedCallback() {
      // Defer init: when this snippet renders N line items in a streaming HTML parse, the
      // 2nd+ element's connectedCallback can fire before its child buttons are parsed.
      queueMicrotask(() => this._init());
    }

    _init() {
      if (this._initialized) return;
      this.quantityDisplay = this.querySelector('[data-quantity]');
      this.increaseBtn = this.querySelector('[data-action="increase"]');
      this.decreaseBtn = this.querySelector('[data-action="decrease"]');
      if (!this.increaseBtn || !this.decreaseBtn || !this.quantityDisplay) return;

      this.lineKey = this.dataset.lineKey;
      this.min = parseInt(this.dataset.min) || 1;
      this.step = parseInt(this.dataset.step) || 1;
      this.max = this.dataset.max ? parseInt(this.dataset.max) : null;

      this.increaseBtn.addEventListener('click', () => this.changeQuantity(1));
      this.decreaseBtn.addEventListener('click', () => this.changeQuantity(-1));

      this._initialized = true;
      this.updateUI();
    }

    get quantity() {
      return parseInt(this.quantityDisplay.textContent);
    }

    updateUI() {
      const qty = this.quantity;

      if (this.max && qty >= this.max) {
        this.increaseBtn.disabled = true;
      } else {
        this.increaseBtn.disabled = false;
      }

      // Minus stops at min; trash button is the explicit remove path.
      if (qty <= this.min) {
        this.decreaseBtn.disabled = true;
      } else {
        this.decreaseBtn.disabled = false;
      }
    }

    changeQuantity(delta) {
      const currentQty = this.quantity;
      let newQty = currentQty + delta * this.step;

      if (newQty < this.min) return;
      if (this.max && newQty > this.max) newQty = this.max;

      if (newQty !== currentQty) {
        this._changeLineItemQuantity(newQty);
      }
    }

    removeItem() {
      return this._changeLineItemQuantity(0);
    }

    async _changeLineItemQuantity(targetQuantity) {
      const lineItem = this.closest('line-item');

      lineItem?.dispatchEvent(
        new CustomEvent('line-item:will-change', {
          bubbles: true,
          detail: { targetQuantity },
        })
      );

      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        })
      );

      this.increaseBtn.disabled = true;
      this.decreaseBtn.disabled = true;

      try {
        const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: this.lineKey,
            quantity: targetQuantity,
            sections: sectionsToBundle,
          }),
        });

        if (!response.ok) {
          const responseContent = await response.json();
          const container = this.closest('.custom-line-item');
          container?.querySelector('[role="alert"]')?.remove();
          const errorSvg = `<svg width="14" viewBox="0 0 18 18">
            <path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="#eb001b"></path>
            <path d="M5.29289 6.70711L11.2929 12.7071L12.7071 11.2929L6.70711 5.29289L5.29289 6.70711ZM6.70711 12.7071L12.7071 6.70711L11.2929 5.2929L5.29289 11.2929L6.70711 12.7071Z" fill="#ffffff"></path>
          </svg>`;
          container
            ?.querySelector('.custom-line-item__info')
            ?.insertAdjacentHTML(
              'beforeend',
              `<p class="text-with-icon text-error text-xs" role="alert">${errorSvg} ${responseContent['description']}</p>`
            );
          lineItem?.dispatchEvent(new CustomEvent('line-item:error', { bubbles: true }));
          this.updateUI();
        } else {
          const cartContent = await response.json();
          const lineItemAfterChange = cartContent['items'].filter((item) => item['key'] === this.lineKey);
          const actualQuantity = lineItemAfterChange.length === 0 ? 0 : lineItemAfterChange[0]['quantity'];

          this.quantityDisplay.textContent = actualQuantity;
          this.updateUI();

          lineItem?.dispatchEvent(
            new CustomEvent('line-item:change', {
              bubbles: true,
              detail: { quantity: actualQuantity, cart: cartContent },
            })
          );

          document.documentElement.dispatchEvent(
            new CustomEvent('cart:change', {
              bubbles: true,
              detail: { baseEvent: 'line-item:change', cart: cartContent },
            })
          );
        }
      } catch (error) {
        console.error('Error updating cart:', error);
        this.updateUI();
      }
    }
  }

  window.customElements.define('custom-line-item-quantity', CustomLineItemQuantity);
}

if (!window.customElements.get('custom-line-item')) {
  class CustomLineItem extends HTMLElement {
    connectedCallback() {
      // Wire the trash/remove button to call removeItem() on the inner quantity component.
      this.addEventListener('click', (event) => {
        const removeBtn = event.target.closest('[data-action="remove"]');
        if (!removeBtn) return;
        event.preventDefault();
        this.querySelector('custom-line-item-quantity')?.removeItem();
      });
    }
  }
  window.customElements.define('custom-line-item', CustomLineItem);
}
